import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { ApiError } from '../../lib/api';
import {
  datetimeLocalToIso,
  isoToDatetimeLocal,
} from '../../lib/sydneyDateTime';
import {
  getPlan,
  listBrandCampaigns,
  updatePlan,
  type PlanUpdatePayload,
} from './api';

export function PlanEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const planQuery = useQuery({
    queryKey: ['plan', id],
    queryFn: () => getPlan(id!),
    enabled: !!id,
  });

  const [name, setName] = useState('');
  const [overview, setOverview] = useState('');
  const [promoStartDate, setPromoStartDate] = useState('');
  const [promoEndDate, setPromoEndDate] = useState('');
  const [offerMechanicsUrl, setOfferMechanicsUrl] = useState('');
  const [kyckCampaignOverviewUrl, setKyckCampaignOverviewUrl] = useState('');
  const [rolloutSlug, setRolloutSlug] = useState('');
  const [monthYearLabel, setMonthYearLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [defaultCampaignId, setDefaultCampaignId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // Names for the Default Campaign dropdown — limited to currently-attached
  // Campaigns (Consolidation §2.3). Brand-visible set, same source the
  // attach picker uses.
  const brandCampaigns = useQuery({
    queryKey: ['plan-brand-campaigns', planQuery.data?.brandAccountId],
    queryFn: () => listBrandCampaigns(planQuery.data!.brandAccountId),
    enabled: !!planQuery.data?.brandAccountId,
  });

  // Seed the form once from the loaded plan. Guarded so a background refetch
  // doesn't clobber in-progress edits.
  useEffect(() => {
    if (hydrated || !planQuery.data) return;
    const p = planQuery.data;
    setName(p.name);
    setOverview(p.overview ?? '');
    setPromoStartDate(isoToDatetimeLocal(p.promoStartDate));
    setPromoEndDate(isoToDatetimeLocal(p.promoEndDate));
    setOfferMechanicsUrl(p.offerMechanicsUrl ?? '');
    setKyckCampaignOverviewUrl(p.kyckCampaignOverviewUrl ?? '');
    setRolloutSlug(p.rolloutSlug ?? '');
    setMonthYearLabel(p.monthYearLabel ?? '');
    setNotes(p.notes ?? '');
    setDefaultCampaignId(p.defaultCampaignId ?? '');
    setHydrated(true);
  }, [hydrated, planQuery.data]);

  // Consolidation §1.2: once a Rollout exists, the execution-fields freeze
  // (dates, slug, month-year, default Campaign, notes). name / overview /
  // offer URLs stay editable so the Plan remains a live management surface.
  // The server re-enforces this; the form just disables + omits them.
  const datesLocked = planQuery.data?.datesLocked === true;
  const rolloutId = planQuery.data?.rolloutId ?? null;
  const lockedTitle = datesLocked
    ? 'Locked. Edit on the Rollout.'
    : undefined;
  const attachedIds = planQuery.data?.campaignIds ?? [];
  const campaignName = (cid: string) =>
    (brandCampaigns.data ?? []).find((c) => c.id === cid)?.name ??
    '(Campaign unavailable)';

  const dateOrderWarning =
    !datesLocked &&
    promoStartDate &&
    promoEndDate &&
    promoEndDate < promoStartDate
      ? 'End date is before start date — saving anyway.'
      : null;

  const mut = useMutation({
    mutationFn: async () => {
      setError(null);
      const patch: PlanUpdatePayload = {
        // Unlocked fields — always editable, even after Rollout creation.
        name: name.trim(),
        overview: overview.trim() || null,
        offerMechanicsUrl: offerMechanicsUrl.trim() || null,
        kyckCampaignOverviewUrl: kyckCampaignOverviewUrl.trim() || null,
        // Execution-fields — omitted entirely once locked so the lock
        // can't be bypassed even if an input were forced editable.
        ...(datesLocked
          ? {}
          : {
              promoStartDate: datetimeLocalToIso(promoStartDate),
              promoEndDate: datetimeLocalToIso(promoEndDate),
              rolloutSlug: rolloutSlug.trim() || null,
              monthYearLabel: monthYearLabel.trim() || null,
              notes: notes.trim() || null,
              defaultCampaignId: defaultCampaignId || null,
            }),
      };
      return updatePlan(id!, patch);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['plan', id] });
      await qc.invalidateQueries({ queryKey: ['plans'] });
      navigate(`/plans/${id}`);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : String(err)),
  });

  if (planQuery.isLoading) return <CenteredSpinner />;
  if (planQuery.error || !planQuery.data) {
    return (
      <div className="text-red-600 text-sm">
        Plan not found or failed to load.
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Edit plan</h1>
        <p className="text-sm text-slate-600">
          Status changes are made from the plan's status pill, not here.
        </p>
      </div>

      {datesLocked && (
        <div className="mb-6 flex items-start justify-between gap-4 rounded-md border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm text-slate-600">
            A Rollout has been created from this Plan. The promo window,
            slug, month-year, default Campaign and notes are now frozen as
            "what we executed" — edit them on the Rollout. Name, overview
            and the offer URLs stay editable here.
          </p>
          {rolloutId && (
            <Link to={`/rollouts/${rolloutId}`} className="shrink-0">
              <Button type="button" variant="outline">
                View Rollout →
              </Button>
            </Link>
          )}
        </div>
      )}

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) {
            setError('Name is required.');
            return;
          }
          mut.mutate();
        }}
      >
        <Field label="Name" required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Overview">
          <textarea
            value={overview}
            onChange={(e) => setOverview(e.target.value)}
            rows={4}
            placeholder="Brief description of this promo... (optional)"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Rollout slug">
          <input
            type="text"
            value={rolloutSlug}
            disabled={datesLocked}
            title={lockedTitle}
            onChange={(e) => setRolloutSlug(e.target.value)}
            placeholder="snap-au-may-2026"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm font-mono disabled:bg-slate-100 disabled:text-slate-500"
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Promo start datetime">
            <input
              type="datetime-local"
              value={promoStartDate}
              disabled={datesLocked}
              title={
                datesLocked ? 'Locked. Edit on the Rollout.' : undefined
              }
              onChange={(e) => setPromoStartDate(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
            />
          </Field>
          <Field label="Promo end datetime">
            <input
              type="datetime-local"
              value={promoEndDate}
              disabled={datesLocked}
              title={
                datesLocked ? 'Locked. Edit on the Rollout.' : undefined
              }
              onChange={(e) => setPromoEndDate(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
            />
          </Field>
        </div>
        {!datesLocked && (
          <p className="-mt-2 text-xs text-slate-500">
            Stored in Sydney time (AEST +10:00).
          </p>
        )}
        {dateOrderWarning && (
          <p className="text-sm text-amber-700">{dateOrderWarning}</p>
        )}

        <Field label="Month-year label">
          <input
            type="text"
            value={monthYearLabel}
            disabled={datesLocked}
            title={lockedTitle}
            onChange={(e) => setMonthYearLabel(e.target.value)}
            placeholder="May 26"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
          />
        </Field>

        <Field label="Default Campaign">
          <select
            value={defaultCampaignId}
            disabled={datesLocked || attachedIds.length === 0}
            title={
              datesLocked
                ? lockedTitle
                : attachedIds.length === 0
                  ? 'Attach Campaigns first.'
                  : undefined
            }
            onChange={(e) => setDefaultCampaignId(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
          >
            <option value="">— None (rows start unassigned) —</option>
            {attachedIds.map((cid) => (
              <option key={cid} value={cid}>
                {campaignName(cid)}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Notes">
          <textarea
            value={notes}
            disabled={datesLocked}
            title={lockedTitle}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Execution-context notes (optional)"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
          />
        </Field>

        <Field label="Offer mechanics URL">
          <input
            type="url"
            value={offerMechanicsUrl}
            onChange={(e) => setOfferMechanicsUrl(e.target.value)}
            placeholder="https://…"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Kyck Campaign Overview URL">
          <input
            type="url"
            value={kyckCampaignOverviewUrl}
            onChange={(e) => setKyckCampaignOverviewUrl(e.target.value)}
            placeholder="https://…"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/plans/${id}`)}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </span>
      {children}
    </label>
  );
}
